import { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  HiTrash,
  HiReply,
  HiPhotograph,
  HiLocationMarker,
  HiPhone,
  HiUser,
  HiCalendar,
  HiMail,
  HiTruck,
  HiX,
} from 'react-icons/hi';

const API_BASE =  process.env.REACT_APP_API_URL;

// Helper to generate a professional tracking number
function generateTrackingNumber() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const rand = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  return `WAV-${yyyy}${mm}${dd}-${rand}`;
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [loading, setLoading] = useState(true);

  // Shipping modal state
  const [shipModal, setShipModal] = useState({
    open: false,
    orderId: null,
    tracking: '',
  });

  const fetchOrders = () => {
    setLoading(true);
    const endpoint = showTrash
      ? '/api/orders/admin/deleted'
      : '/api/orders/admin';
    api
      .get(endpoint)
      .then((res) => {
        const sorted = [...res.data].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setOrders(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrders();
  }, [showTrash]);

  // ----- Shipping actions -----
  const openShipModal = (orderId) => {
    setShipModal({ open: true, orderId, tracking: generateTrackingNumber() });
  };

  const closeShipModal = () => {
    setShipModal({ open: false, orderId: null, tracking: '' });
  };

  const markAsShipped = async () => {
    const { orderId, tracking } = shipModal;
    try {
      await api.put(`/api/admin/orders/${orderId}/ship`, {
        trackingNumber: tracking || '', // backend will generate if blank
      });
      closeShipModal();
      fetchOrders();
    } catch (err) {
      alert('Failed to update order');
    }
  };

  // Advance status (OutForDelivery or Delivered)
  const advanceStatus = async (orderId, nextStatus) => {
    try {
      await api.put(`/api/admin/orders/${orderId}/advance`, {
        status: nextStatus,           // <-- key is "status"
        location: 'Lagos Warehouse',
      });
      fetchOrders();
    } catch (err) {
      alert('Failed to advance status');
    }
  };

  // ----- Other order actions -----
  const updateStatus = async (orderId, newStatus) => {
    await api.put(`/api/orders/admin/${orderId}/status`, { status: newStatus });
    fetchOrders();
  };

  const softDelete = async (orderId) => {
    if (!window.confirm('Move this order to trash?')) return;
    await api.put(`/api/orders/admin/${orderId}/soft-delete`);
    fetchOrders();
  };

  const restoreOrder = async (orderId) => {
    await api.put(`/api/orders/admin/${orderId}/restore`);
    fetchOrders();
  };

  const permanentDelete = async (orderId) => {
    if (!window.confirm('Permanently delete this order? This cannot be undone.'))
      return;
    await api.delete(`/api/orders/admin/${orderId}/permanent`);
    fetchOrders();
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/uploads/'))
      return `${API_BASE}/images/${url.replace('/uploads/', '')}`;
    return url;
  };

  const statusBadgeClass = (status) => {
    switch (status) {
      case 'PAID':
      case 'SHIPPING':
      case 'DELIVERED':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'CANCELLED':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-[3px] border-gray-200 border-t-black rounded-full" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-bold text-gray-900">Orders</h2>
        <button
          onClick={() => setShowTrash(!showTrash)}
          className="text-sm font-medium text-gray-500 hover:text-black underline underline-offset-4 self-start sm:self-auto"
        >
          {showTrash ? '← Back to Active Orders' : 'View Trash'}
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-100 rounded-2xl">
          <p className="text-gray-500">
            {showTrash ? 'Trash is empty.' : 'No orders yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div
              key={order.id}
              className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              {/* Order header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-xs font-mono text-gray-400 uppercase tracking-wider">
                      Order #{order.id.substring(0, 8)}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                      <HiCalendar size={14} />
                      <span>
                        {new Date(order.createdAt).toLocaleDateString('en-NG', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-600 font-medium">
                      <HiMail size={14} className="text-gray-400" />
                      <span>{order.userEmail}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 self-start sm:self-auto">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${statusBadgeClass(
                      order.status
                    )}`}
                  >
                    {order.status}
                  </span>
                </div>
              </div>

              {/* Customer info */}
              {order.shippingAddress && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Customer
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-start gap-2">
                      <HiUser className="text-gray-400 mt-0.5 flex-shrink-0" size={16} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {order.shippingAddress.fullName ||
                            order.shippingAddress.firstName +
                              ' ' +
                              order.shippingAddress.lastName ||
                            '—'}
                        </p>
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                          <HiLocationMarker size={14} className="text-gray-400" />
                          <span>
                            {[
                              order.shippingAddress.address,
                              order.shippingAddress.city,
                              order.shippingAddress.state,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </div>
                      </div>
                    </div>
                    {order.shippingAddress.phone && (
                      <div className="flex items-center gap-2 sm:justify-end">
                        <HiPhone className="text-gray-400 flex-shrink-0" size={16} />
                        <p className="text-sm text-gray-900">
                          {order.shippingAddress.phone}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Items list */}
              <ul className="mt-5 space-y-3">
                {order.items?.map((item) => (
                  <li key={item.productId} className="flex items-center gap-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                      {item.image ? (
                        <img
                          src={getImageUrl(item.image)}
                          alt={item.productName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <HiPhotograph size={20} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.productName}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {item.size && `${item.size}`}
                        {item.color && ` / ${item.color}`}
                        {` · Qty: ${item.quantity}`}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                      ₦{(item.price * item.quantity).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>

              {/* Footer – total, tracking & actions */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 mt-5 border-t border-gray-100">
                <div>
                  <p className="text-base font-bold text-gray-900">
                    Total: ₦{order.total?.toLocaleString()}
                  </p>
                  {order.trackingNumber && (
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <HiTruck size={14} />
                      <span className="font-mono">{order.trackingNumber}</span>
                      {order.shippingStatus && (
                        <span className="ml-2 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">
                          {order.shippingStatus}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {!showTrash && (
                    <>
                      {/* Show shipping actions based on shippingStatus */}
                      {order.status === 'PAID' && !order.shippingStatus && (
                        <button
                          onClick={() => openShipModal(order.id)}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                        >
                          Mark as Shipped
                        </button>
                      )}
                      {order.shippingStatus === 'Shipped' && (
                        <button
                          onClick={() => advanceStatus(order.id, 'OutForDelivery')}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition"
                        >
                          Out for Delivery
                        </button>
                      )}
                      {order.shippingStatus === 'OutForDelivery' && (
                        <button
                          onClick={() => advanceStatus(order.id, 'Delivered')}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition"
                        >
                          Delivered
                        </button>
                      )}
                      {(order.status === 'PENDING' || order.status === 'PENDING_PAYMENT') && (
                        <button
                          onClick={() => updateStatus(order.id, 'CANCELLED')}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 transition"
                        >
                          Cancel
                        </button>
                      )}
                      {(order.status === 'DELIVERED' || order.status === 'CANCELLED') && (
                        <button
                          onClick={() => softDelete(order.id)}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition flex items-center gap-1"
                        >
                          <HiTrash size={14} /> Move to Trash
                        </button>
                      )}
                    </>
                  )}

                  {showTrash && (
                    <>
                      <button
                        onClick={() => restoreOrder(order.id)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition flex items-center gap-1"
                      >
                        <HiReply size={14} /> Restore
                      </button>
                      <button
                        onClick={() => permanentDelete(order.id)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 transition flex items-center gap-1"
                      >
                        <HiTrash size={14} /> Delete Forever
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Shipping modal */}
      {shipModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 transform transition-all">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Shipping Confirmation</h3>
              <button onClick={closeShipModal} className="text-gray-400 hover:text-gray-600">
                <HiX size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              This will mark the order as shipped and send an email to the customer with the tracking link.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tracking Number</label>
            <input
              type="text"
              value={shipModal.tracking}
              onChange={(e) => setShipModal({ ...shipModal, tracking: e.target.value })}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 outline-none mb-5"
              placeholder="WAV-20260805-0001"
            />
            <p className="text-xs text-gray-400 mb-6">
              Leave blank to auto‑generate a tracking number.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={closeShipModal}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={markAsShipped}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition shadow-sm"
              >
                Confirm & Ship
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}